import React, { Fragment, ReactNode, useState } from 'react';

import { differenceInDays, startOfDay } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { AvatarPropTypes } from '@atlaskit/avatar';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import Heading from '@atlaskit/heading';
import { Box, Grid, Inline, Text, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { ActivityEvent } from 'src/components/activity/pr-event';
import { ActivityOverviewEntry } from 'src/components/activity/types';
import GenericMessage from 'src/components/generic-message';
import isMobile from 'src/components/sidebar/src/is-mobile';
import { User } from 'src/components/types';
import { UserAvatar } from 'src/containers/user-avatar';
import { UserName } from 'src/containers/user-name';
import { MediaAuth } from 'src/hooks/media';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { ActivityPanelTitle } from '../tabs/overview/activity-panel/activity-panel-title';

import { messages as eventMessages } from './pull-request-activity-events.i18n';
import {
  ALLOWABLE_ACTIVITY_TYPES,
  FilterOptions,
  SelectableFilterOptions,
} from './pull-request-activity-filter/constants';
import { PullRequestOverviewActivityFilter } from './pull-request-activity-filter/pull-request-overview-activity-filter';
import messages, { filterEmptyMessages } from './pull-request-activity.i18n';
import * as styles from './pull-request-activity.styled';
import { PullRequestAddGlobalConversation } from './pull-request-add-global-conversation';
import PullRequestApprovalEvent from './pull-request-approval-event';
import {
  PullRequestAttachmentBody,
  PullRequestAttachmentTitle,
} from './pull-request-attachment-entry';
import PullRequestChangesRequestedEvent from './pull-request-changes-requested-event';
import { PullRequestCommentEntry } from './pull-request-comment-activity';
import { PullRequestCommentTitle } from './pull-request-comment-title';
import { PullRequestCommitEntry } from './pull-request-commit-entry';
import { PullRequestDraftEntry } from './pull-request-draft-entry';
import {
  PullRequestReviewersAddedBody,
  PullRequestReviewersAddedTitle,
} from './pull-request-reviewers-added-entry';
import { PullRequestStatusEntry } from './pull-request-status-entry';
import {
  PullRequestTasksBody,
  PullRequestTasksTitle,
} from './pull-request-task-entry';
import { TimelineBox } from './timeline-box';

export type PullRequestOverviewActivityProps = {
  activityEntries: ActivityOverviewEntry[];
  hasError: boolean;
  isLoading: boolean;
  retryFetchActivity: () => void;
  uuid?: string;
  mediaAuth: MediaAuth;
};

function ActivityEntryCard(props: { activity: ReactNode }) {
  return (
    <Box padding="space.100" style={{ textAlign: 'left' }}>
      {props.activity}
    </Box>
  );
}

export function PullRequestOverviewActivity(
  props: PullRequestOverviewActivityProps
) {
  const [filterOption, setFilterOption] = useState<FilterOptions>(
    FilterOptions.SHOW_ALL
  );

  const renderActorAvatar = (
    actor: User | undefined,
    avatarProps: Partial<AvatarPropTypes>
  ) => (
    <UserAvatar
      {...avatarProps}
      profileCardPosition="left-start"
      user={actor}
    />
  );

  const renderActorName = (actor: User | undefined) => (
    <Text weight="semibold">
      <UserName profileCardPosition="left-start" user={actor} />
    </Text>
  );

  const renderLoadingState = () => {
    return (
      <styles.Spinner>
        <Spinner size="small" delay={0} />
      </styles.Spinner>
    );
  };

  const renderFooter = () => {
    const { hasError, retryFetchActivity } = props;

    if (hasError) {
      return (
        <GenericMessage
          iconType="warning"
          title={<FormattedMessage {...messages.activityErrorHeading} />}
          messageType="activity"
        >
          <CustomThemeButton appearance="link" onClick={retryFetchActivity}>
            <FormattedMessage {...messages.activityErrorAction} />
          </CustomThemeButton>
        </GenericMessage>
      );
    }

    return null;
  };

  const getActivityEntryNodes = (
    entry: ActivityOverviewEntry
  ): { title: ReactNode; children?: ReactNode } => {
    switch (entry.type) {
      case 'comment-start': {
        return {
          title: (
            <PullRequestCommentTitle latestComment={entry.latestComment} />
          ),
          children: (
            <PullRequestCommentEntry
              event={entry.event}
              actor={entry.actor}
              timestamp={entry.timestamp}
            />
          ),
        };
      }
      case 'approval':
        return { title: <PullRequestApprovalEvent /> };
      case 'changes-requested':
        return { title: <PullRequestChangesRequestedEvent /> };
      case 'status-change':
        return {
          title: (
            <PullRequestStatusEntry
              status={entry.event.update.state}
              draft={entry.event.update?.draft}
            />
          ),
        };
      case 'update':
        if ('hashes' in entry) {
          return { title: <PullRequestCommitEntry hashes={entry.hashes} /> };
        }
        return { title: null };
      case 'title-change':
        return {
          title: (
            <Fragment>
              <FormattedMessage {...eventMessages.titleChangeMessage} />:{' '}
              <s>{entry.event.update.changes.title?.old}</s>{' '}
              {entry.event.update.changes.title?.new}
            </Fragment>
          ),
        };
      case 'description-change':
        return {
          title: (
            <FormattedMessage {...eventMessages.descriptionChangeMessage} />
          ),
        };
      // plural task events happen in the new activity feed
      case 'tasks-created':
      case 'tasks-resolved':
        return {
          title: <PullRequestTasksTitle event={entry.event} />,
          children: <PullRequestTasksBody event={entry.event} />,
        };
      case 'reviewers-added':
        return {
          title: <PullRequestReviewersAddedTitle event={entry.event} />,
          children: <PullRequestReviewersAddedBody event={entry.event} />,
        };
      case 'attachment-added':
        return {
          title: (
            <PullRequestAttachmentTitle
              numberOfAttachments={entry.items.length}
            />
          ),
          children: (
            <PullRequestAttachmentBody
              isLoading={props.mediaAuth.isLoading}
              mediaClientConfig={props.mediaAuth.mediaClientConfig}
              attachments={entry.items}
              collectionName={props.mediaAuth.collectionName || ''}
            />
          ),
        };
      case 'draft-change':
        return {
          title: <PullRequestDraftEntry draft={entry.event.update.draft} />,
        };
    }
    return { title: null };
  };

  const renderActivityEntry = (entry: ActivityOverviewEntry) => {
    const { timestamp, actor } = entry;
    const key = `${timestamp.getTime()}-${entry.type}`;

    const { title, children } = getActivityEntryNodes(entry);
    return (
      <ActivityEvent
        actor={actor || undefined}
        date={timestamp}
        key={key}
        renderActorAvatar={renderActorAvatar}
        renderActorName={renderActorName}
        title={title}
      >
        {children}
      </ActivityEvent>
    );
  };

  // Even though filterOption is in scope, we pass the selected option in
  // because `SelectableFilterOptions` is more constrained than
  // `FilterOptions`.
  const getActivitiesFilter = (option: SelectableFilterOptions) => {
    if (option === FilterOptions.MY_COMMENTS) {
      return (activityEntry: ActivityOverviewEntry) =>
        ALLOWABLE_ACTIVITY_TYPES[option].includes(activityEntry.type) &&
        activityEntry.actor.uuid === props.uuid;
    }

    return (activityEntry: ActivityOverviewEntry) =>
      ALLOWABLE_ACTIVITY_TYPES[option].includes(activityEntry.type);
  };

  const renderActivities = () => {
    let { activityEntries } = props;

    if (filterOption !== FilterOptions.SHOW_ALL) {
      activityEntries = activityEntries.filter(
        getActivitiesFilter(filterOption)
      );

      if (!activityEntries.length && filterOption !== FilterOptions.STATUS) {
        return (
          <styles.EmptyState>
            <FormattedMessage {...filterEmptyMessages[filterOption]} />
          </styles.EmptyState>
        );
      }
    }

    const relativeDateStr = (d: Date): string => {
      const daysAgo = differenceInDays(new Date(), d);
      if (daysAgo === 0) {
        return 'Today';
      }
      if (daysAgo === 1) {
        return 'Yesterday';
      }
      if (daysAgo < 7) {
        return d.toLocaleDateString(undefined, { weekday: 'long' });
      }
      return d.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

    // We're going to group the entries by date.
    //   * Key: Milliseconds representing the start of the day
    //   * Value: Array of ActivityEntry objects within that day
    const groupedEntries = new Map<number, ActivityOverviewEntry[]>();
    for (const entry of activityEntries) {
      const date = startOfDay(entry.timestamp).getTime();
      if (!groupedEntries.has(date)) {
        groupedEntries.set(date, []);
      }
      groupedEntries.get(date)!.push(entry);
    }

    // Return a timeline segment for each day
    return Array.from(groupedEntries).map(([dateMillis, entries]) => (
      <Box key={dateMillis} xcss={xcss({ marginBottom: 'space.200' })}>
        <Heading size="xxsmall">
          <span style={{ color: token('color.text.subtle', colors.N200) }}>
            {relativeDateStr(new Date(dateMillis))}
          </span>
        </Heading>

        <Grid templateColumns="50px minmax(0, 1fr)" autoFlow="row">
          {entries.map((entry, index) => (
            <Fragment key={index}>
              <TimelineBox
                entry={entry}
                entryIndex={index}
                entryCount={entries.length}
              />
              <ActivityEntryCard activity={renderActivityEntry(entry)} />
            </Fragment>
          ))}
        </Grid>
      </Box>
    ));
  };

  const onFilterStateChange = (option: FilterOptions) => {
    setFilterOption(option);
    publishUiEvent({
      action: 'activityFilterChanged',
      actionSubject: 'pullRequestActivityFeed',
      actionSubjectId: filterOption,
      source: 'prView',
      attributes: {
        activityFeedVersion: 'new',
      },
    });
  };

  const { isLoading, uuid } = props;

  return (
    <div data-qa="pr-activity">
      {isMobile() ? (
        <PullRequestOverviewActivityFilter
          uuid={uuid}
          isDisabled={isLoading}
          onChangeState={onFilterStateChange}
        />
      ) : (
        <Inline grow="fill" spread="space-between" alignBlock="center">
          <ActivityPanelTitle />
          <PullRequestOverviewActivityFilter
            uuid={uuid}
            isDisabled={isLoading}
            onChangeState={onFilterStateChange}
          />
        </Inline>
      )}
      <PullRequestAddGlobalConversation />
      {renderActivities()}
      {isLoading ? renderLoadingState() : renderFooter()}
    </div>
  );
}
